// SEAT-DRAWER-UX §3 (docs/design/2026-10-28-seat-drawer-ux.md): the drawer's header.
//   - the title: a manager edits it inline (a pencil on hover and focus; Enter saves
//     `seats.update { title }` under the seat's version, Esc cancels, a toast confirms, the chart
//     refreshes); anyone else reads it;
//   - the DE definition line, for a seat a digital employee holds: Role · Goal · Orders · How I
//     work, each into the DE page's Setup, one external arrow at the end.
import { Button, cn, Input, toast } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PencilIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { seatQuery, structureKeys } from "../queries";
import { FormError } from "../structure/parts";
import { chartKeys } from "./chart-queries";
import { PageArrow } from "./section-modal";

export function SeatTitle({
  api,
  company,
  seatId,
  title,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  title: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  // The seat's version when the edit began (the chart does not carry it): a rename made
  // elsewhere while the input is open is a conflict, not silently overwritten.
  // A save made before the read lands waits for it.
  const base = useRef<Promise<number> | null>(null);
  const begin = () => {
    setDraft(title);
    const read = qc
      .fetchQuery({ ...seatQuery(api, company.id, seatId), staleTime: 0 })
      .then((s) => s.version);
    read.catch(() => {});
    base.current = read;
  };
  const save = useMutation({
    mutationFn: async (next: string) => {
      if (!base.current) throw new Error("Start the rename again.");
      const version = await base.current;
      return api.client.seats.update({
        params: { co: company.id, id: seatId },
        ifMatch: version,
        body: { title: next },
      });
    },
    onSuccess: () => {
      setDraft(null);
      base.current = null;
      toast.add({ title: "Seat renamed", type: "success" });
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
      void qc.invalidateQueries({ queryKey: structureKeys.seat(company.id, seatId) });
    },
  });
  if (!canEdit)
    return <span className="text-base leading-6 font-semibold break-words">{title}</span>;
  if (draft !== null) {
    const next = draft.trim();
    return (
      <form
        className="flex min-w-0 flex-col gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (next && next !== title && !save.isPending) save.mutate(next);
          else if (next === title) setDraft(null);
        }}
      >
        <Input
          autoFocus
          aria-label="Seat title"
          className="h-8 text-base font-semibold"
          value={draft}
          disabled={save.isPending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setDraft(null);
              save.reset();
            }
          }}
          onBlur={() => {
            if (!save.isPending && (!next || next === title)) setDraft(null);
          }}
          data-slot="seat-title-input"
        />
        <FormError what="Renaming the seat" error={save.error} />
      </form>
    );
  }
  return (
    <span className="group/title flex min-w-0 items-start gap-1">
      <button
        type="button"
        className="min-w-0 rounded-sm text-left text-base leading-6 font-semibold break-words focus-visible:outline-2 focus-visible:outline-ring"
        onClick={begin}
        title="Rename the seat"
        data-slot="seat-title"
      >
        {title}
      </button>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={`Rename ${title}`}
        className={cn(
          "mt-0.5 text-faint hover:text-foreground",
          "opacity-0 group-hover/title:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
        )}
        onClick={begin}
        data-slot="seat-title-edit"
      >
        <PencilIcon aria-hidden="true" />
      </Button>
    </span>
  );
}

/** The DE page's sections a definition link opens (`?tab=` names the section). */
const DEFINITION: { label: string; tab: "profile" | "job" }[] = [
  { label: "Role", tab: "profile" },
  { label: "Goal", tab: "profile" },
  { label: "Orders", tab: "job" },
  { label: "How I work", tab: "profile" },
];

/** "Definition: Role · Goal · Orders · How I work ↗" for a seat a DE holds. */
export function DefinitionLine({
  company,
  deMembershipId,
}: {
  company: CompanyAccess;
  deMembershipId: string;
}) {
  return (
    <span
      className="flex flex-wrap items-center gap-x-1 text-xs text-faint"
      data-slot="de-definition"
    >
      Definition:
      {DEFINITION.map((d, i) => (
        <span key={d.label} className="inline-flex items-center">
          {i > 0 ? <span aria-hidden="true">·&nbsp;</span> : null}
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug: company.slug, id: deMembershipId }}
            search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), tab: d.tab })}
            className="text-primary-text hover:underline"
            data-page-link=""
          >
            {d.label}
            {i === DEFINITION.length - 1 ? <PageArrow /> : null}
          </Link>
        </span>
      ))}
    </span>
  );
}
